import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { cpf } from 'cpf-cnpj-validator';

import { Dialog } from 'primereact/dialog';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { DataTable } from 'primereact/datatable';
import { SplitButton } from 'primereact/splitbutton';
import { Calendar } from 'primereact/calendar';
import { InputMask } from 'primereact/inputmask';
import { Toast } from 'primereact/toast';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';

import { useData } from 'src/contexts/data';
import { dateToString } from 'src/utils/dateToString';
import { ModelOfVehicleMovimentation } from 'src/database';
import LoadingScreen from 'src/components/LoadingScreen';

import { useVehicleReportsController } from 'src/controllers/DataReports/Fleet/VehicleReport';
import { useFormCreateMovimentation } from 'src/controllers/Movimentation/CreateMovimentationController';
import { useListUnitysController } from 'src/controllers/Unitys/ListUnitysController';
import { useListProductsController } from 'src/controllers/Products/ListProductsController';

import './styles.scss';

const VehicleMovimentations: React.FC = () => {
	const { getVehicleReport, handleVehicleReport } = useVehicleReportsController();
	const { vehicleDataSelected } = useData();

	const [period, setPeriod] = useState<Date | Date[]>();

	const dataTable: any = useRef(null);

	const exportCSV = () => {
		dataTable.current.exportCSV();
	};

	const exportItems = [
		{
			label: 'Exportar PDF',
			icon: 'pi pi-upload',
			command: () => {
				handleVehicleReport.generateVehicleMovimentationReport(
					getVehicleReport.vehicleMovimentationData,
					period
				);
			},
		},
		{
			label: 'Exportar CSV',
			icon: 'pi pi-upload',
			command: exportCSV,
		},
	];

	return (
		<div>
			{getVehicleReport.loading ? <LoadingScreen color='#0388e5' /> : <></>}
			<div style={{ height: '100%' }} className='card p-grid p-ml-0 p-mr-0 p-d-flex p-align-center'>
				<div className='p-col-12 p-md-2'>
					<span className='info-report-title'>Secretaria: </span>
					<span className='info-report-text'>{vehicleDataSelected?.NOME_ORGAOUNIDADE}</span>
				</div>

				<div className='p-col-12 p-md-3'>
					<span className='info-report-title'>Veículo: </span>
					<span className='info-report-text'>{vehicleDataSelected?.DESCRICAO}</span>
				</div>

				<div className='p-col-12 p-md-1'>
					<span className='info-report-title'>Placa: </span>
					<span className='info-report-text'>
						{vehicleDataSelected !== undefined ? vehicleDataSelected?.PLACA : null}
					</span>
				</div>

				<div className='p-col-12 p-md-3'>
					<Calendar
						id='periodo'
						placeholder='Selecionar período'
						className='info-report-text'
						showIcon
						icon='pi pi-calendar'
						value={period}
						onChange={(e: any) => {
							setPeriod(e.target.value);
						}}
						view='month'
						dateFormat='mm/yy'
						yearNavigator
						yearRange='2010:2030'
					/>
				</div>

				<div className='p-col-12 p-md-3 p-m-0 p-p-0 p-d-flex p-justify-end p-align-end'>
					<></>
					<SplitButton
						className='p-col-12'
						model={exportItems}
						icon='pi pi-search'
						label='Puxar relatório'
						onClick={() => {
							if (vehicleDataSelected?.ID) {
								handleVehicleReport.getVehicleMovimentationData(vehicleDataSelected?.ID, period);
							}
						}}
					/>
				</div>
			</div>

			<ReportsComponent tableRef={dataTable} movimentationData={getVehicleReport.vehicleMovimentationData} />
		</div>
	);
};

interface VehicleDataProps {
	movimentationData: ModelOfVehicleMovimentation[];
	tableRef: any;
}

interface filteredFieldProps {
	label: string;
	value?: string;
}

const ReportsComponent: React.FC<VehicleDataProps> = ({ movimentationData, tableRef }) => {
	const { getDemandFormController, handleDemandFormController } = useFormCreateMovimentation();
	const { getUnityListController } = useListUnitysController();
	const { getProductListController } = useListProductsController();

	const checkCpfCnpj = (cpfInput: string) => cpf.isValid(cpfInput);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [cpfOriginPerson, setCpfOriginPerson] = useState('');
	const [cpfDestinyPerson, setCpfDestinyPerson] = useState('');

	const [valueUnity, setValueUnity] = useState('');
	const [filteredUnity, setFilteredUnity] = useState<filteredFieldProps[]>([{ label: '', value: '' }]);
	const [valueProduct, setValueProduct] = useState('');
	const [filteredProduct, setFilteredProduct] = useState<filteredFieldProps[]>([{ label: '', value: '' }]);

	const searchUnitys = (event: any) => {
		let filteredUnitys;
		if (!event.query.trim().length) {
			filteredUnitys = [...unitys];
		} else {
			filteredUnitys = unitys.filter((unity) => {
				return unity.label.toLowerCase().includes(event.query.toLowerCase());
			});
		}

		setFilteredUnity(filteredUnitys);
	};

	const searchProducts = (event: any) => {
		let filteredProducts;
		if (!event.query.trim().length) {
			filteredProducts = [...products];
		} else {
			filteredProducts = products.filter((product) => {
				return product.label.toLowerCase().includes(event.query.toLowerCase());
			});
		}

		setFilteredProduct(filteredProducts);
	};

	const unitys = getUnityListController.AllUnitysData.map((unity) => {
		return { label: unity.NOME, value: unity.ID };
	});

	const products = getProductListController.allProductsData.map((product) => {
		return { label: product.DESCRICAO, value: product.ID };
	});

	const handleDialog = () => {
		setDialogOpen(!dialogOpen);
	};

	const tableHeader = (
		<div className='table-header p-grid p-d-flex p-jc-between'>
			<h5 className='p-m-0'>Relatório de Inventário</h5>
		</div>
	);

	const HEADER_DATA = (rowData: ModelOfVehicleMovimentation) => {
		return (
			<React.Fragment>
				<span className='left-header'>DATA</span>
				{dateToString(rowData.DATA, 'br')}
			</React.Fragment>
		);
	};

	const HEADER_QUILOMETRAGEM = (rowData: ModelOfVehicleMovimentation) => {
		return (
			<React.Fragment>
				<span className='left-header'>KM DE ABASTECIMENTO</span>
				{rowData.QUILOMETRAGEM}
			</React.Fragment>
		);
	};

	const HEADER_QUANTIDADELITROS = (rowData: ModelOfVehicleMovimentation) => {
		return (
			<React.Fragment>
				<span className='left-header'>QUANTIDADE ABASTECIDA</span>
				{rowData.QUANTIDADELITROS}
			</React.Fragment>
		);
	};

	const HEADER_VALORABASTECIMENTO = (rowData: ModelOfVehicleMovimentation) => {
		return (
			<React.Fragment>
				<span className='left-header'>VALOR ABASTECIMENTO</span>
				{rowData.VALORABASTECIMENTO}
			</React.Fragment>
		);
	};

	const HEADER_MOTORISTA = (rowData: ModelOfVehicleMovimentation) => {
		return (
			<React.Fragment>
				<span className='left-header'>MOTORISTA</span>
				{rowData.MOTORISTA}
			</React.Fragment>
		);
	};

	const leftProductBrandToolbar = () => {
		return (
			<Button
				label='Cadastrar movimentação'
				icon='pi pi-plus'
				className='p-button-success p-mr-2'
				onClick={() => {
					handleDialog();
				}}
			/>
		);
	};

	const createMovimentationDialogFooter = (
		<React.Fragment>
			<Button
				label='Cancelar'
				icon='pi pi-times'
				className='p-button-text'
				onClick={() => {
					handleDialog();
				}}
			/>
			<Button
				type='button'
				label='Salvar'
				icon='pi pi-check'
				className='p-button-text'
				onClick={() => {
					getDemandFormController.formik.handleSubmit();
				}}
			/>
		</React.Fragment>
	);

	return (
		<div className='p-card p-p-4'>
			<Toast ref={getDemandFormController.toast} />
			<Toolbar className='p-mb-4' left={leftProductBrandToolbar}></Toolbar>

			<DataTable
				ref={tableRef}
				header={tableHeader}
				className='responsive-table p-datatable-sm'
				dataKey='ID'
				autoLayout
				value={movimentationData}>
				<Column field='QUILOMETRAGEM' header='KM ABASTECIMENTO' body={HEADER_QUILOMETRAGEM}></Column>
				<Column field='QUANTIDADELITROS' header='QUANTIDADE ABASTECIDA' body={HEADER_QUANTIDADELITROS}></Column>
				<Column
					field='VALORABASTECIMENTO'
					header='VALOR ABASTECIMENTO'
					body={HEADER_VALORABASTECIMENTO}></Column>
				<Column field='DATA' header='DATA' body={HEADER_DATA}></Column>
				<Column field='MOTORISTA' header='MOTORISTA' body={HEADER_MOTORISTA}></Column>
			</DataTable>

			<Dialog
				className='p-fluid dropdown-visible'
				style={{ width: '90%' }}
				visible={dialogOpen}
				onHide={() => {
					handleDialog();
					getDemandFormController.formik.resetForm();
				}}
				footer={createMovimentationDialogFooter}
				header={`Registrar movimentação`}
				modal>
				{getDemandFormController.loading ? <LoadingScreen color='#0388e5' /> : <></>}
				<div className='p-grid p-fluid'>
					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='cpf1'>CPF do requisitante</label>
						<InputMask
							id='cpf1'
							autoClear={false}
							mask='999.999.999-99'
							placeholder='___.___.___-__'
							value={cpfOriginPerson}
							onChange={(e) => setCpfOriginPerson(e.value)}
							onComplete={(e: any) => {
								handleDemandFormController.searchPersonCpf(e.value, 'PESSOAORIGEM');
							}}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getDemandFormController.formik.touched.PESSOAORIGEM &&
									getDemandFormController.formik.errors.PESSOAORIGEM,
								'popup-form-inactive': !(
									getDemandFormController.formik.touched.PESSOAORIGEM &&
									getDemandFormController.formik.errors.PESSOAORIGEM
								),
							})}>
							{!checkCpfCnpj(cpfOriginPerson)
								? '* Informe CPF válido'
								: getDemandFormController.formik.errors.PESSOAORIGEM}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='cpf2'>CPF do responsável</label>
						<InputMask
							id='cpf2'
							autoClear={false}
							mask='999.999.999-99'
							placeholder='___.___.___-__'
							value={cpfDestinyPerson}
							onChange={(e) => setCpfDestinyPerson(e.value)}
							onComplete={(e: any) =>
								handleDemandFormController.searchPersonCpf(e.value, 'PESSOADESTINO')
							}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getDemandFormController.formik.touched.PESSOADESTINO &&
									getDemandFormController.formik.errors.PESSOADESTINO,
								'popup-form-inactive': !(
									getDemandFormController.formik.touched.PESSOADESTINO &&
									getDemandFormController.formik.errors.PESSOADESTINO
								),
							})}>
							{!checkCpfCnpj(cpfDestinyPerson)
								? '* Informe CPF válido'
								: getDemandFormController.formik.errors.PESSOADESTINO}
						</div>
					</div>

					<div className='p-field p-col-6'>
						<label htmlFor='unidade'>Unidade</label>
						<AutoComplete
							id='unidade'
							value={valueUnity}
							field='label'
							placeholder='Buscar...'
							onChange={(e) => {
								setValueUnity(e.value);
								getDemandFormController.formik.setFieldValue(
									'ORGAOUNIDADEDESTINO',
									filteredUnity.find((unity) => {
										return valueUnity.toLowerCase() === unity.label.toLowerCase();
									})?.value || ''
								);
							}}
							onSelect={(e) => {
								setValueUnity(e.value.label);
								getDemandFormController.formik.setFieldValue('ORGAOUNIDADEDESTINO', e.value.value);
							}}
							suggestions={filteredUnity}
							completeMethod={searchUnitys}
							dropdown
							dropdownMode='current'
							minLength={2}
							delay={0}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getDemandFormController.formik.touched.ORGAOUNIDADEDESTINO &&
									getDemandFormController.formik.errors.ORGAOUNIDADEDESTINO,
								'popup-form-inactive': !(
									getDemandFormController.formik.touched.ORGAOUNIDADEDESTINO &&
									getDemandFormController.formik.errors.ORGAOUNIDADEDESTINO
								),
							})}>
							{getDemandFormController.formik.errors.ORGAOUNIDADEDESTINO}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='produto'>Produto de licitação utilizado</label>
						<AutoComplete
							id='produto'
							value={valueProduct}
							field='label'
							placeholder='Buscar...'
							onChange={(e) => {
								setValueProduct(e.value);
								getDemandFormController.formik.setFieldValue(
									'AX_PRODUTO',
									filteredProduct.find((Product) => {
										return valueProduct.toLowerCase() === Product.label.toLowerCase();
									})?.value || ''
								);
								handleDemandFormController.handleFindInfo(
									'AX_PRODUTO',
									filteredProduct.find((Product) => {
										return valueProduct.toLowerCase() === Product.label.toLowerCase();
									})?.value || ''
								);
							}}
							onSelect={(e) => {
								setValueProduct(e.value.label);
								getDemandFormController.formik.setFieldValue('AX_PRODUTO', e.value.value);
								handleDemandFormController.handleFindInfo('AX_PRODUTO', e.value.value);
							}}
							suggestions={filteredProduct}
							completeMethod={searchProducts}
							dropdown
							dropdownMode='current'
							minLength={2}
							delay={0}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getDemandFormController.formik.touched.AX_PRODUTO &&
									getDemandFormController.formik.errors.AX_PRODUTO,
								'popup-form-inactive': !(
									getDemandFormController.formik.touched.AX_PRODUTO &&
									getDemandFormController.formik.errors.AX_PRODUTO
								),
							})}>
							{getDemandFormController.formik.errors.AX_PRODUTO}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='dataefetivada'>Data do movimento</label>
						<InputMask
							id='dataefetivada'
							mask='99/99/9999'
							placeholder='__/__/____'
							{...getDemandFormController.formik.getFieldProps('DATAEFETIVADA')}
							onChange={(e) => getDemandFormController.formik.setFieldValue('DATAEFETIVADA', e.value)}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getDemandFormController.formik.touched.DATAEFETIVADA &&
									getDemandFormController.formik.errors.DATAEFETIVADA,
								'popup-form-inactive': !(
									getDemandFormController.formik.touched.DATAEFETIVADA &&
									getDemandFormController.formik.errors.DATAEFETIVADA
								),
							})}>
							{getDemandFormController.formik.errors.DATAEFETIVADA}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='quantidade'>Quantidade abastecida (L)</label>
						<InputText
							id='quantidade'
							maxLength={25}
							onChange={getDemandFormController.formik.handleChange('QUANTIDADEAUTORIZADA')}
							value={getDemandFormController.formik.values.QUANTIDADEAUTORIZADA}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getDemandFormController.formik.touched.QUANTIDADEAUTORIZADA &&
									getDemandFormController.formik.errors.QUANTIDADEAUTORIZADA,
								'popup-form-inactive': !(
									getDemandFormController.formik.touched.QUANTIDADEAUTORIZADA &&
									getDemandFormController.formik.errors.QUANTIDADEAUTORIZADA
								),
							})}>
							{getDemandFormController.formik.errors.QUANTIDADEAUTORIZADA}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='veiculokm'>Quilometragem do veículo</label>
						<InputText
							id='veiculokm'
							maxLength={25}
							onChange={getDemandFormController.formik.handleChange('VEICULOKM')}
							value={getDemandFormController.formik.values.VEICULOKM}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getDemandFormController.formik.touched.VEICULOKM &&
									getDemandFormController.formik.errors.VEICULOKM,
								'popup-form-inactive': !(
									getDemandFormController.formik.touched.VEICULOKM &&
									getDemandFormController.formik.errors.VEICULOKM
								),
							})}>
							{getDemandFormController.formik.errors.VEICULOKM}
						</div>
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default VehicleMovimentations;
